import { useEffect, useRef, useMemo } from "react";
import { useWatch } from "react-hook-form";
import type { Control, UseFormSetValue } from "react-hook-form";
import type { AdverseEventNewFormData } from "../RegisterNewForm/RegisterNewForm.types";
import {
  useGetEventMainFactors,
  useGetEventSpecificFactors,
  useGetAdverseEventInterventionLevels,
} from "../../../../shared/services/catalogService";

interface UseAdverseEventFactorsFormFieldsProps {
  control: Control<AdverseEventNewFormData>;
  setValue: UseFormSetValue<AdverseEventNewFormData>;
  /** Numeric adverse event type ID (Accidente=1, Incidentes=2). Drives Q3 options. */
  adverseEventsTypeId: number | null;
}

/**
 * Manages the three-question cascade for Accidente and Incidentes adverse events.
 *
 * Q1: Factor principal — always fetched on mount (static catalog).
 * Q2: Factor específico — fetched when Q1 is answered; filtered by main_factor_id.
 * Q3: Nivel de intervención — fetched when mounted; filtered by adverse_events_type_id.
 *
 * Reset rules:
 * - Q3 resets when adverseEventsTypeId changes (Accidente ↔ Incidente have different levels).
 * - Q2 resets when the Q1 selection changes.
 */
export const useAdverseEventFactorsFormFields = ({
  control,
  setValue,
  adverseEventsTypeId,
}: UseAdverseEventFactorsFormFieldsProps) => {
  const selectedMainFactorId = useWatch({
    control,
    name: "event_main_factor_id",
  });

  const mainFactorIdNum = selectedMainFactorId
    ? parseInt(selectedMainFactorId, 10)
    : null;

  const { data: eventMainFactors = [], isLoading: isLoadingMainFactors } =
    useGetEventMainFactors();

  const { data: eventSpecificFactors = [], isLoading: isLoadingSpecificFactors } =
    useGetEventSpecificFactors(mainFactorIdNum);

  const { data: interventionLevels = [], isLoading: isLoadingInterventionLevels } =
    useGetAdverseEventInterventionLevels(adverseEventsTypeId);

  // Store the initial values so we can detect real changes vs. mount/remount.
  // Initializing with the current value means the effect never fires when the
  // value hasn't actually changed (handles React Strict Mode double-invoke too).
  const prevAdverseEventsTypeId = useRef(adverseEventsTypeId);
  const prevMainFactorId = useRef(selectedMainFactorId);

  // Reset Q3 only when the adverse event type ACTUALLY changes after mount.
  useEffect(() => {
    if (prevAdverseEventsTypeId.current === adverseEventsTypeId) {
      prevAdverseEventsTypeId.current = adverseEventsTypeId;
      return;
    }
    prevAdverseEventsTypeId.current = adverseEventsTypeId;
    setValue("adverse_event_intervention_level_id", "");
  }, [adverseEventsTypeId, setValue]);

  // Reset Q2 only when Q1 ACTUALLY changes after mount.
  useEffect(() => {
    if (prevMainFactorId.current === selectedMainFactorId) {
      prevMainFactorId.current = selectedMainFactorId;
      return;
    }
    prevMainFactorId.current = selectedMainFactorId;
    setValue("event_specific_factor_id", "");
  }, [selectedMainFactorId, setValue]);

  const mainFactorOptions = useMemo(
    () =>
      [...eventMainFactors]
        .map((f) => ({ label: f.name, value: f.id.toString() })),
    [eventMainFactors]
  );

  const specificFactorOptions = useMemo(
    () =>
      [...eventSpecificFactors]
        .sort((a, b) => b.id - a.id)
        .map((f) => ({ label: f.name, value: f.id.toString() })),
    [eventSpecificFactors]
  );

  const interventionLevelOptions = useMemo(
    () =>
      [...interventionLevels]
        .sort((a, b) => b.id - a.id)
        .map((l) => ({ label: l.name, value: l.id.toString() })),
    [interventionLevels]
  );

  return {
    mainFactorOptions,
    specificFactorOptions,
    interventionLevelOptions,
    isQ1Answered: !!selectedMainFactorId,
    isLoadingMainFactors,
    isLoadingSpecificFactors,
    isLoadingInterventionLevels,
  };
};
